import {Box, Checkbox, FormControlLabel, Stack} from "@mui/material";
import SignaturePad, {SignaturePadRef} from "crqlar-react-common/dist/components/SignaturePad";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SurveyRunnerMarkdown from "../SurveyRunnerMarkdown";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import {surveyControls} from "../surveyRunnerColors";

const fileToDataUrl = (file: File) => new Promise<string | undefined>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
    reader.onerror = () => resolve(undefined);
    reader.readAsDataURL(file);
});

function LegalQuestion({question, localize, language, value, onAnswerChange, services}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localAccepted, setLocalAccepted] = useState(false);
    const padRef = useRef<SignaturePadRef>(null);

    const accepted = value?.booleanValue ?? localAccepted;

    const questionId = question.id;
    const requireSignature = !!question.requireSignature;
    const registerSignatureCapture = services?.registerSignatureCapture;

    useEffect(() => {
        if (!registerSignatureCapture || !questionId || !requireSignature) return;

        registerSignatureCapture(questionId, async () => {
            const file = await padRef.current?.getSignatureFile();
            return file ? fileToDataUrl(file) : undefined;
        });

        return () => registerSignatureCapture(questionId, undefined);
    }, [registerSignatureCapture, questionId, requireSignature]);

    const accept = (next: boolean) => {
        setLocalAccepted(next);
        onAnswerChange?.({...value, booleanValue: next});
    };

    const markSignature = (empty: boolean) => {
        onAnswerChange?.({...value, signatureDrawn: !empty, signatureValue: undefined});
    };

    const terms = localize(question.termsText);
    const agreement = localize(question.agreementLabel);

    return (
        <Stack gap={1.5}>
            {!!terms.trim() && (
                <Box
                    sx={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        textAlign: 'left',
                        mb: 1,
                    }}
                >
                    <SurveyRunnerMarkdown markdown={terms} fontSize={16} color={'text.primary'}/>
                </Box>
            )}

            <FormControlLabel
                sx={{m: 0, alignItems: 'flex-start'}}
                control={
                    <Checkbox
                        checked={accepted}
                        onChange={(event) => accept(event.target.checked)}
                    />
                }
                label={
                    <Box
                        sx={{py: 1.125}}
                    >
                        <SurveyRunnerMarkdown
                            markdown={agreement || t('I have read and agree to the terms and conditions')}
                            fontSize={16}
                        />
                    </Box>
                }
            />

            {requireSignature && (
                <Box sx={theme => ({
                    mt: 2.5, touchAction: 'none', overscrollBehavior: 'contain',
                    '& .w-signature': {
                        fill: theme.palette.text.primary,
                        outline: `1px solid ${surveyControls(theme).border}`,
                    },
                })}>
                    <SignaturePad
                        ref={padRef}
                        questionId={questionId ?? 'preview'}
                        value={value?.signatureValue}
                        onEmptyChange={markSignature}
                    />
                </Box>
            )}
        </Stack>
    );
}

export default LegalQuestion;
